// The chart colours of the Analytics hub, read from PA's tokens (src/styles/contract.css and the Signal DS tokens.css).
// Stands in for the clinician demo's src/design/contract.ts and src/lib/design-tokens.ts (@661ec4c), which hold the same
// values as hex. ECharts cannot read a CSS variable, so each value is resolved from the document when a chart option
// is built: the same keys the demo's options read (`TEXT.secondary`, `CHART.series[0]`, `CATEGORY.jade[500]`), each one
// a token. Outside a browser (or before the sheet loads) a value reads as '' and ECharts falls back to its default.
//
// Token for each demo value:
//   TEXT.body / secondary / meta   --ink-body / --ink-secondary / --ink-meta
//   SURFACE.read / line / app      --surface-read / --surface-line / --surface-app
//   CHART.series[0] / [1]          --accent-primary / --color-blue-400 (the demo's #3391F9). The demo's third series
//                                  colour (#C875A3) has no token and no chart on these two tabs uses it.
//   CHART.grid / targetBand        --chart-grid / --chart-target-band
//   CATEGORY.<hue>[step]           --cat-<hue>-<step> (the muted Okabe-Ito ramp, same values in both apps)
//   colors.blue[100 / 300 / 600]   --color-blue-100 / --color-blue-300 / --color-blue-600

function cssVar(name: string): string {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export const TEXT = {
  get body() { return cssVar('--ink-body'); },
  get secondary() { return cssVar('--ink-secondary'); },
  get meta() { return cssVar('--ink-meta'); },
};

export const SURFACE = {
  get app() { return cssVar('--surface-app'); },
  get panel() { return cssVar('--surface-panel'); },
  get read() { return cssVar('--surface-read'); },
  get line() { return cssVar('--surface-line'); },
};

export const CHART = {
  get series(): readonly string[] { return [cssVar('--accent-primary'), cssVar('--color-blue-400')]; },
  get meaning() { return { good: cssVar('--status-good'), attention: cssVar('--status-attention'), bad: cssVar('--status-bad') }; },
  get targetBand() { return cssVar('--chart-target-band'); },
  get grid() { return cssVar('--chart-grid'); },
  labelPx: 12,
  areaOpacity: 0.12,
};

export type CategoryHue = 'orange' | 'jade' | 'purple' | 'vermilion' | 'sky' | 'yellow' | 'blue';
type CategoryStep = 100 | 200 | 400 | 500 | 600 | 800;

const ramp = (hue: CategoryHue) => new Proxy({} as Record<CategoryStep, string>, {
  get: (_t, step) => cssVar(`--cat-${hue}-${String(step)}`),
});

export const CATEGORY: Record<CategoryHue, Record<CategoryStep, string>> = {
  orange: ramp('orange'),
  jade: ramp('jade'),
  purple: ramp('purple'),
  vermilion: ramp('vermilion'),
  sky: ramp('sky'),
  yellow: ramp('yellow'),
  blue: ramp('blue'),
};

/** The blue ramp steps the heatmap reads. */
export const colors = {
  blue: {
    get 100() { return cssVar('--color-blue-100'); },
    get 300() { return cssVar('--color-blue-300'); },
    get 600() { return cssVar('--color-blue-600'); },
  },
};

/** [size px, line-height px, weight] of the text roles the chart theme reads (the six PA type roles). */
export const TYPE_ROLES = {
  cardTitle: [16, 24, 600],
  meta: [12, 16, 400],
} as const;
